"use client";

// Lib

import { Vector3, VideoTexture, WebGLRenderer, ACESFilmicToneMapping } from "three";
import { PageType, POIType } from "@/types";
import { Canvas } from "@react-three/fiber";
import { motion } from "framer-motion";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { TransformControls } from "@react-three/drei";
import "react-toastify/dist/ReactToastify.css";

import { Slide, ToastContainer, Zoom, cssTransition, toast } from "react-toastify";

// Icons

import CloseIcon from "@/app/svg/CloseIcon";

// Style
import styles from "@/styles/Canvas.module.scss";

// comps
import POI from "./Poi";
import Model3D from "./Model3D";
import { RichText } from "@/components/StrapiComponents/RichText";
import CameraBackground, { ICameraBackground } from "./CameraBackground";

// Dev

import AnimatedCamera from "./AnimatedCamera";
import ScreenshotButton from "./ScreenshotButton";

// PostEffects
import EnvironmentBackground from "./EnviromentBackground";
import Loader from "./Loader";
import { VideoComponent } from "./VideoComponent";
import { HandleRendering } from "./HandleRendering";

import * as THREE from "three";

import { NavigationHelper } from "./NavigationHelper";
import { isTouchDevice } from "@/app/common/Utils";
import { CameraIcon } from "./Icons/CameraIcon";
import { RotateCameraIcon } from "./Icons/RotateCameraIcon";
import AdminPanel from "@/adminArea/AdminPanel";
import { ImageLoader } from "./ContentSections/ImageLoader";

// r150
THREE.ColorManagement.enabled = true;

// r139-r149
(THREE.ColorManagement as unknown as any).legacyMode = false;

const Fade = cssTransition({
	enter: "errorfadeIn",
	exit: "errorfadeOut",
});

export default function CanvasComponent({ page }: { page: PageType }) {
	const [showVideo, setShowVideo] = useState(false);
	const [tryVideoStream, setTryVideoStream] = useState(false);
	const [videoTexture, setVideoTexture] = useState<VideoTexture | null>(null); // Neuer State für VideoTexture

	const [selectedPOI, setSelectedPOI] = useState<POIType | null>(null);
	const animatedCameraRef = useRef<any>();
	const modelRef = useRef<any>();
	const container = useRef(null);
	const cameraBGRef = useRef<ICameraBackground>(null);
	const glRef = useRef<WebGLRenderer | null>(null);
	const [isInitialized, setInit] = useState<boolean>(false);
	const [showPOINavi, setShowPOINavi] = useState<boolean>(false);

	const [realVideoIsPlaying, setRealVideoIsPlaying] = useState<boolean>(true);
	const [isFadingOut, setIsFadingOut] = useState<boolean>(false);

	// Admin
	const [adminPOI, setAdminPOI] = useState<POIType | null>(null);
	const adminTransformRef = useRef<any>();

	// Tooltip
	const [showToolTip, setShowTooltip] = useState<boolean>(false);
	const [tooltipVisible, SetTooltipVisible] = useState<boolean>(false);

	const [isTouch, setIsTouch] = useState(false);

	const isAndroid = useMemo(() => {
		if (typeof window !== "undefined") {
			return /Android/i.test(navigator.userAgent);
		}
		return false;
	}, []);

	useEffect(() => {
		setIsTouch(isTouchDevice());
	}, []);

	// const isTouch = isTouchDevice();

	const handlePOIClick = (poi: POIType) => {
		setSelectedPOI(null);

		//Falls bereits ein POI ausgewählt ist warte einen kurzen moment
		if (selectedPOI) {
			setTimeout(() => {
				if (animatedCameraRef.current) {
					animatedCameraRef.current.moveToPosition(poi.camera_position as Vector3, poi.target as Vector3, () => {
						setSelectedPOI(poi);
					});
				}
			}, 250);
		} else {
			if (animatedCameraRef.current) {
				animatedCameraRef.current.moveToPosition(poi.camera_position as Vector3, poi.target as Vector3, () => {
					setSelectedPOI(poi);
				});
			}
		}
	};

	const onAnimationDone = () => {
		setShowPOINavi(true);
	};

	useEffect(() => {
		if (typeof document === "undefined") {
			return;
		}
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible" && !showPOINavi && isInitialized) {
				setShowPOINavi(true);
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [showPOINavi, isInitialized]);

	const closePOI = (moveCamera?: boolean) => {
		setSelectedPOI(null);

		setTimeout(() => {
			if (typeof moveCamera === "undefined" || moveCamera) {
				animatedCameraRef.current.moveToPosition(page.default_camera_position as Vector3, page.default_camera_target as Vector3);
			}
		}, 500);
	};

	const onModelLoad = () => {
		if (animatedCameraRef.current && !isInitialized && modelRef.current) {
			setInit(true);
		}
	};

	useEffect(() => {
		if (!showPOINavi) {
			setSelectedPOI(null);
		}

		if (!showVideo) {
			setVideoTexture(null);
		}
	}, [showPOINavi, showVideo]);

	const onVideoLoad = (videoTexture: VideoTexture) => {
		setVideoTexture(videoTexture);
		setTryVideoStream(false);
	};

	const onVideoError = () => {
		setVideoTexture(null);
		setShowVideo(false);
		setTryVideoStream(false);

		toast.dismiss();

		toast.error("Leider konnte die Kamera nicht gestartet werden. Bitte überprüfe deine Kameraeinstellungen.", {
			position: "top-center",
			autoClose: 5000,
			hideProgressBar: true,
			closeOnClick: false,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			closeButton: false,
			theme: "dark",
			transition: Fade,
		});
	};

	const onTryVideoStream = () => {
		setTryVideoStream(true);
	};

	const handleControlChange = () => {
		if (selectedPOI) {
			closePOI(false);
		}
	};

	const selectedAdminPOI = (poi: POIType | null) => {
		setAdminPOI(poi);
	};

	const handleVideoEnded = () => {
		// console.log("Video ist fertig CanvasComponent");
		setIsFadingOut(true);

		//Camera zu position
		if (animatedCameraRef.current) {
			animatedCameraRef.current.moveToPosition(page.default_camera_position as Vector3, page.default_camera_target as Vector3, () => {
				setShowPOINavi(true);
			});
		}
	};

	const ToggleCamera = () => {
		// console.log("TOggleCamera", cameraBGRef.current);
		if (cameraBGRef.current) {
			cameraBGRef.current.toggleCamera();
		}
	};

	const handleToolTip = () => {
		// console.log("Show tooltip again");

		closePOI();
		setShowTooltip(true);
	};

	const GetReflection = () => {
		if (page.reflection !== null) {
			return process.env.NEXT_PUBLIC_API_ORIGIN + page.reflection.url;
		}
		if (page.background !== null) {
			return process.env.NEXT_PUBLIC_API_ORIGIN + page.background.url;
		}
		return null;
	};

	const handleShowRealWorld = () => {
		setShowVideo(!showVideo);
	};

	useEffect(() => {
		if (showVideo) {
			document.body.classList.add("fullscreen");
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: "instant",
			});
		} else {
			document.body.classList.remove("fullscreen");
		}
	}, [showVideo]);

	/**
	 *  Loose WebGL - Context
	 *
	 */
	useEffect(() => {
		if (typeof document === "undefined" || typeof window === "undefined") {
			return;
		}

		const canvas = document.querySelector("canvas");
		const handleContextLost = (event: Event) => {
			console.log("WebGL context lost");
			event.preventDefault();
		};

		const handleContextRestored = () => {
			console.log("WebGL context restored");
		};

		if (canvas) {
			canvas.addEventListener("webglcontextlost", handleContextLost);
			canvas.addEventListener("webglcontextrestored", handleContextRestored);
		}

		return () => {
			if (canvas) {
				canvas.removeEventListener("webglcontextlost", handleContextLost);
				canvas.removeEventListener("webglcontextrestored", handleContextRestored);
			}
		};
	}, []);

	/**
	 *  Back to Top Listener
	 *
	 */
	useEffect(() => {
		const handleBackToTop = () => {
			window.scrollTo({ top: 0, behavior: "smooth" });

			handleVideoEnded();
		};
		if (typeof document !== "undefined" && typeof window !== "undefined") {
			document.addEventListener("backToTop", handleBackToTop);

			return () => {
				document.removeEventListener("backToTop", handleBackToTop);
			};
		}
	}, []);

	return (
		<>
			<ToastContainer
				position="top-center"
				autoClose={5000}
				hideProgressBar
				newestOnTop={false}
				closeOnClick={false}
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="color"
				transition={Slide}
			/>

			<div
				className={`${styles.wrapper} ${showVideo ? styles.wrapperFullScreen : ""}`}
				// style={page.background?.url ? { backgroundImage: `url(${process.env.NEXT_PUBLIC_API_ORIGIN + page.background?.url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
			>
				{/* <AnimatePresence> */}
				{realVideoIsPlaying && (
					<motion.div
						className={`${styles.videoWrapper}`}
						initial={{ opacity: 1 }}
						key="video-component"
						animate={isFadingOut ? { opacity: 0 } : { opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.5 }}
						onAnimationComplete={() => {
							if (isFadingOut) {
								setRealVideoIsPlaying(false); // Setze erst nach der Ausblendung
							}
						}}
					>
						<VideoComponent page={page} onEnd={handleVideoEnded} />
					</motion.div>
				)}

				<motion.div
					key="canvas-component"
					className={styles.canvasWrapper}
					initial={{ opacity: 0.001 }}
					animate={realVideoIsPlaying ? { opacity: 0 } : { opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.5 }}
				>
					<Canvas
						ref={container}
						dpr={isAndroid ? [1, 1.5] : [0.8, 2]}
						style={{ width: "100%", height: "100%" }}
						gl={{ powerPreference: "high-performance" }}
						onCreated={({ gl }) => {
							glRef.current = gl;
							gl.physicallyCorrectLights = false; // Aktivieren der physikalisch korrekten Lichter
							gl.toneMapping = ACESFilmicToneMapping;
							gl.toneMappingExposure = 1;
						}}
					>
						<HandleRendering />

						{adminPOI && (
							<TransformControls position={[adminPOI.position.x, adminPOI.position.y, adminPOI.position.z]} ref={adminTransformRef}>
								<mesh scale={1}>
									<sphereGeometry args={[0.15, 32, 32]} />
									<meshStandardMaterial color={"white"} />
								</mesh>
							</TransformControls>
						)}
						{/** Lights */}
						{/* <ambientLight intensity={1} /> */}
						{/* <directionalLight intensity={2} /> */}
						{/* <pointLight intensity={1} position={[0, 2, 2]} decay={0.1} /> */}
						<AnimatedCamera ref={animatedCameraRef} onChange={handleControlChange} page={page} />
						<Suspense fallback={null}>
							{/* Kamera-Feed als Hintergrund */}
							{page.background && (
								<CameraBackground
									url={process.env.NEXT_PUBLIC_API_ORIGIN + page.background.url}
									showVideo={showVideo}
									ref={cameraBGRef}
									onVideoLoad={onVideoLoad}
									onVideoError={onVideoError}
									onTryVideoStream={onTryVideoStream}
								/>
							)}
						</Suspense>

						{
							/**
							 * POIs werden nur auf Desktop gezeigt
							 *
							 * */
							!tooltipVisible &&
								!isTouch &&
								showPOINavi &&
								page &&
								!showVideo &&
								page.pois.length &&
								page.pois.map((poi) => {
									return selectedPOI?.id === poi.id ? null : <POI poiContent={poi} key={poi.id} onPOIClick={handlePOIClick} />;
								})
						}

						<EnvironmentBackground image={GetReflection()} video={videoTexture} page={page} />

						{page.model.url && (
							<Suspense fallback={<Loader />}>
								<Model3D page={page} freezeTime={page.animation_freeze} url={page.model.url} ref={modelRef} onLoad={onModelLoad} onAnimationDone={onAnimationDone} />
							</Suspense>
						)}
					</Canvas>
				</motion.div>

				{/** ------------------------------------------------------------  */}
				{/* <AnimatePresence> */}
				{selectedPOI && (
					<motion.section key="poi-3d-navigation" className={styles.overlayWrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
						<div className={styles.overlay}>
							<button className={`${styles.closeButton}`} onClick={() => closePOI()}>
								<CloseIcon fill="#000" />
							</button>
							<header className={`flex flex-end ${styles.overlayHeader}`}></header>
							<section className="translate">
								{/* <h1>{selectedPOI.title}</h1> */}
								<RichText content={selectedPOI.content} />
							</section>
						</div>
					</motion.section>
				)}

				{/** --------------- Während des Wartens auf den Videostream von der Camera ----------------  */}

				{tryVideoStream && (
					<motion.section key="camera-wait-for-stream" className={styles.overlayWrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
						<ImageLoader />
					</motion.section>
				)}

				{/** Touch Navigation Hilfe */}

				{showPOINavi && <NavigationHelper show={showToolTip} setShow={(bool) => setShowTooltip(bool)} visibleMethod={(visible) => SetTooltipVisible(visible)} />}

				{/** Zeige POI Navi erst nachdem die Animation einmal abgespielt wurde */}

				<motion.div
					className={styles.navigation}
					key="poi-navigation"
					initial={{ opacity: 0 }}
					animate={showPOINavi && !tooltipVisible ? { opacity: 1 } : { opacity: 0 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 1 }}
				>
					{showPOINavi && (
						<div className={styles.navigationElement}>
							{!showVideo && !isTouch && (
								<>
									<div className={styles.poiNavigation}>
										{page.pois.map((poi, index) => (
											<button
												title={`Wissenssnack ${index + 1}`}
												disabled={selectedPOI?.id === poi.id}
												className={`${styles.poiButton} ${selectedPOI?.id === poi.id ? styles.active : ""}`}
												key={poi.id}
												onClick={() => handlePOIClick(poi)}
											>
												<div className={styles.poiButtonInner}></div>
											</button>
										))}
									</div>
								</>
							)}

							{isTouch && (
								<div className="ar-button-wrapper translate">
									<button className="ar-button uppercase bold translate" onClick={handleShowRealWorld}>
										{showVideo ? null : <CameraIcon fill="#fff" />}
										<span>{showVideo ? "Zurück zur Ausstellung" : "In deine Welt bringen"}</span>
									</button>
									{cameraBGRef.current && cameraBGRef.current.getDeviceCount() > 1 && showVideo && (
										<>
											<button className="tooltip" name="Switch Kamera" onClick={ToggleCamera}>
												<RotateCameraIcon />
											</button>
										</>
									)}

									<button className="tooltip" name="Öffne Tooltips" onClick={handleToolTip}>
										?
									</button>
								</div>
							)}

							{showVideo && isTouch && <ScreenshotButton glRef={glRef} page={page} />}

							{!showVideo && !isTouch && (
								<button className="tooltip" name="Öffne Tooltips" onClick={handleToolTip}>
									?
								</button>
							)}
						</div>
					)}
				</motion.div>
				{/* </AnimatePresence> */}
			</div>
			{process.env.NODE_ENV !== "production" && (
				<AdminPanel page={page} modelRef={modelRef} animatedCameraRef={animatedCameraRef} selected_admin_POI={adminPOI} selectPOI={selectedAdminPOI} adminTransformRef={adminTransformRef} />
			)}
		</>
	);
}
